import { Component, Input, Output, EventEmitter } from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import * as Highcharts from "highcharts";
import { VAIconRegistryService, arrowToBottom } from "@va/core-library/icons";

@Component({
  selector: "app-chart-content",
  templateUrl: "./chart-content.component.html",
  styleUrls: ["./chart-content.component.scss"],
})
export class ChartContentComponent {
  @Input() currentIndex = 0;
  @Input() currentItem;
  @Input() chartOptions;
  @Input() chartType = "trend";
  @Input() viewportOptions;
  @Input() periodLabel;
  @Output() isDownloadingInProgress: EventEmitter<any> = new EventEmitter();

  dataToUse = {};
  inViewportChange;
  toLoad;
  selectedChartType;
  chartTypeMapping = {
    trend: "comparative-page-timeseries",
    comparison: "comparative-page-comparison",
    revision: "comparative-page-revision",
  };
  downloadOptVisible: boolean = false;
  chartInstance: Highcharts.Chart;
  chartInfoBottom = "0px";
  isSecondaryAxisUpdateInProgress = false;
  isSecondaryAxis;
  allowCombineAxis = true;
  axisChange = false;

  constructor(private iconRegistry: VAIconRegistryService) {
    this.inViewportChange = new Subject<any>().pipe(debounceTime(240));

    this.inViewportChange.subscribe((isInViewport) => {
      if (isInViewport && !this.toLoad) {
        this.toLoad = true;
      }
    });
    this.iconRegistry.registerIcons([arrowToBottom]);
  }

  ngOnInit() {}

  ngOnChanges(changes) {
    if (
      changes.chartType &&
      changes.chartType.currentValue !== changes.chartType.previousValue
    ) {
      this.selectedChartType = this.chartTypeMapping[this.chartType];
    }
    if (
      changes.chartOptions &&
      changes.chartOptions.currentValue !== changes.chartOptions.previousValue
    ) {
      const dataToUse = { ...this.chartOptions };

      const currentKey =
        this.selectedChartType === this.chartTypeMapping.comparison
          ? this.chartOptions.periods[this.currentIndex]
          : this.chartOptions.linesData[this.currentIndex];

      let selectedTickers = [];
      if (this.chartOptions.tickerType === "custom") {
        selectedTickers = this.chartOptions.tickerData.custom || [];
      } else if (this.chartOptions.tickerType === "top_ten") {
        selectedTickers = this.chartOptions.tickerData[currentKey] || [];
      }
      const tickerIds = [];
      const tickerInfo = [];
      for (const item of selectedTickers) {
        tickerIds.push(item.ticker);
        tickerInfo.push({
          ticker: item.ticker,
          name: item.full_name,
          cid: item.cid || item.company_id || item.companyid,
        });
      }

      if (
        this.chartOptions.shouldShowAverage &&
        this.selectedChartType !== this.chartTypeMapping.comparison
      ) {
        const averageId = "Average";
        tickerIds.push(averageId);
        tickerInfo.push({ ticker: averageId, name: averageId });
      }
      dataToUse["tickers"] = tickerIds;
      dataToUse["selectedTickersInfo"] = tickerInfo;
      dataToUse["lines"] = [currentKey];
      dataToUse["lineInfo"] = {
        [currentKey]: this.chartOptions.lineInfoData[currentKey],
      };

      if (this.selectedChartType === this.chartTypeMapping.comparison) {
        this.axisChange = false;
        dataToUse["lines"] = this.chartOptions.linesData;
        dataToUse["lineInfo"] = this.chartOptions.lineInfoData;
        dataToUse["periods"] = [currentKey];
        if (["LTM", "NTM", "STM"].includes(currentKey)) {
          dataToUse["periods"] = [];
          dataToUse["otherPeriods"] = [currentKey];
        } else {
          dataToUse["otherPeriods"] = [];
        }
        if (parseInt(this.chartOptions.scaleType, 10) !== 1) {
          this.axisChange = true;
          this.isSecondaryAxis = false;
          this.allowCombineAxis = true;
        } else {
          if (
            this.chartOptions.linesData.length > 1 &&
            this.chartOptions.lineInfoData[this.chartOptions.linesData[0]]
              .unit ===
              this.chartOptions.lineInfoData[this.chartOptions.linesData[1]]
                .unit &&
            this.chartOptions.lineInfoData[this.chartOptions.linesData[0]]
              .magnitude ===
              this.chartOptions.lineInfoData[this.chartOptions.linesData[1]]
                .magnitude
          ) {
            this.axisChange = true;
            this.isSecondaryAxis = false;
            this.allowCombineAxis = true;
          } else {
            this.axisChange = true;
            this.isSecondaryAxis = true;
            this.allowCombineAxis = false;
          }
        }
        dataToUse["isSecondaryAxis"] = this.isSecondaryAxis;

        if (
          changes.chartOptions &&
          changes.chartOptions.previousValue &&
          changes.chartOptions.currentValue &&
          changes.chartOptions.previousValue.scaleType !==
            changes.chartOptions.currentValue.scaleType
        ) {
          if (
            (this.chartOptions.linesData.length > 1 &&
              this.chartOptions.lineInfoData[this.chartOptions.linesData[0]]
                .unit !==
                this.chartOptions.lineInfoData[this.chartOptions.linesData[1]]
                  .unit) ||
            this.axisChange
          ) {
            this.chartInfoBottom = "0px";
            dataToUse["operation"] = "fetchData";
          }
        }
      }
      this.dataToUse = dataToUse;
    }
  }

  onInViewportChange(isInViewport) {
    this.inViewportChange.next(isInViewport);
  }

  /**
   * Executes on click of Download Chart.
   * It uses chartInstance from HighChart and performs the action depending on download type
   * @param {number} downloadType - type of download
   */
  downloadValue(downloadType) {
    this.isDownloadingInProgress.emit(true);
    this.downloadOptVisible = false;
    const secondName =
      this.selectedChartType === this.chartTypeMapping.comparison
        ? this.periodLabel[this.currentIndex]
        : `${this.dataToUse["lineInfo"][this.currentItem]["formatted_name"]}`;
    const chartName = `${this.dataToUse["chartName"]} - ${secondName}`;
    this.chartInstance.options.title.text = chartName;
    switch (downloadType) {
      case 0:
        this.chartInstance.exportChart(
          {
            type: "image/png",
            filename: chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 1:
        this.chartInstance.exportChart(
          {
            type: "image/jpeg",
            filename: chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 2:
        this.chartInstance.exportChart(
          {
            type: "application/pdf",
            filename: chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 3:
        this.chartInstance.exportChart(
          {
            type: "image/svg+xml",
            filename: chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 4:
        this.chartInstance.print();
        break;
      default:
        break;
    }
  }

  onExport() {
    this.downloadOptVisible = false;
    let name = "";
    let selectedPeriods = [];
    const secondName =
      this.selectedChartType === this.chartTypeMapping.comparison
        ? this.periodLabel[this.currentIndex]
        : `${this.dataToUse["lineInfo"][this.currentItem]["formatted_name"]}`;
    const chartName = `${this.dataToUse["chartName"]} - ${secondName}`;
    selectedPeriods =
      this.dataToUse["periods"] && this.dataToUse["periods"].length
        ? this.dataToUse["periods"]
        : this.dataToUse["otherPeriods"];
    const xlsxRows = [
      [
        { type: "string", value: "Chart Name" },
        { type: "string", value: chartName },
      ],
      [
        { type: "string", value: "Currency" },
        { type: "string", value: this.dataToUse["currency"] },
      ],
      [
        { type: "string", value: "Period" },
        { type: "string", value: selectedPeriods[0] },
      ],
      [
        { type: "string", value: "Rebase to 100" },
        {
          type: "string",
          value:
            this.dataToUse["scaleType"] === 2 ? "Index to 100" : "Absolute",
        },
      ],
      [
        { type: "string", value: "" },
        { type: "string", value: "" },
      ],
    ];
    if (this.dataToUse["scaleType"] !== 2) {
      if (
        (this.dataToUse["chartTypeId"] !== "3" &&
          this.dataToUse["dataView"] !== "yoy") ||
        this.dataToUse["chartTypeId"] === "3"
      ) {
        xlsxRows.splice(4, 0, [
          { type: "string", value: "" },
          { type: "string", value: `All numbers in M unless stated otherwise` },
        ]);
      }
    }
    this.dataToUse["chartTypeId"] !== "3" &&
      xlsxRows.splice(3, 0, [
        { type: "string", value: "Values or YoY%" },
        { type: "string", value: this.dataToUse["dataView"] },
      ]);
    this.dataToUse["chartTypeId"] === "3" &&
      xlsxRows.splice(3, 0, [
        { type: "string", value: "Revision History" },
        {
          type: "string",
          value: this.dataToUse["revision"].startsWith("custom")
            ? this.dataToUse["revision"].split("|")[1]
            : this.dataToUse["revision"].replace("m", " months"),
        },
      ]);
    this.dataToUse["chartTypeId"] === "2" &&
      xlsxRows.splice(2, 1, [
        { type: "string", value: "Period" },
        {
          type: "string",
          value: `${selectedPeriods[0]} - ${
            selectedPeriods[selectedPeriods.length - 1]
          }`,
        },
      ]);
    switch (this.dataToUse["chartTypeId"]) {
      case "1":
        {
          const headerRow = this.dataToUse["tickers"].map((item) => {
            return { type: "string", value: item };
          });
          headerRow.unshift({ type: "string", value: "" });
          xlsxRows.push(headerRow);
          const rows = this.chartInstance.options.series.map((item) => {
            const magnitude = `${
              this.dataToUse["lineInfo"][item.custom.parentLine] &&
              this.dataToUse["lineInfo"][item.custom.parentLine].magnitude &&
              this.dataToUse["lineInfo"][item.custom.parentLine].magnitude !==
                "" &&
              this.dataToUse["lineInfo"][item.custom.parentLine].magnitude !==
                "Abs" &&
              this.dataToUse["scaleType"] !== 2 &&
              this.dataToUse["dataView"] !== "yoy"
                ? `(in ${
                    this.dataToUse["lineInfo"][this.currentItem].magnitude
                  })`
                : ""
            }`;
            const row = [];
            row.push({ type: "string", value: `${item.name}${magnitude}` });
            item["data"].map((d) => {
              const type = typeof d.y === "number" ? "number" : "string";
              row.push({ type: type, value: d.y });
              return d;
            });
            return row;
          });
          rows.map((row) => {
            xlsxRows.push(row);
            return row;
          });
        }
        break;
      case "2":
        {
          let tsRow = [];
          const headRow = [{ type: "string", value: "Ticker" }];
          this.chartInstance.xAxis[0].categories.map((x) =>
            headRow.push({ type: "string", value: x }),
          );

          this.dataToUse["lines"].map((item) => {
            const magnitude = `${
              this.dataToUse["lineInfo"][this.currentItem] &&
              this.dataToUse["lineInfo"][this.currentItem].magnitude &&
              this.dataToUse["lineInfo"][this.currentItem].magnitude !== "" &&
              this.dataToUse["lineInfo"][this.currentItem].magnitude !==
                "Abs" &&
              this.dataToUse["scaleType"] !== 2 &&
              this.dataToUse["dataView"] !== "yoy"
                ? `(in ${
                    this.dataToUse["lineInfo"][this.currentItem].magnitude
                  })`
                : ""
            }`;
            xlsxRows.push([]);
            xlsxRows.push([
              {
                type: "string",
                value: `${this.dataToUse["lineInfo"][item]["name"]}${magnitude}`,
              },
            ]);
            xlsxRows.push(headRow);
            return item;
          });
          this.chartInstance.options.series.map((item) => {
            // if (item.name !== "") {
            tsRow = [];
            tsRow.push({ type: "string", value: item.name });
            item["data"].map((val) => {
              tsRow.push({
                type: "string",
                value: val.y ? val.y.toFixed(2) : val.y,
              });
              return val.y;
            });
            xlsxRows.push(tsRow);
            // }
            return item;
          });
        }

        break;
      case "3":
        {
          const row = [];
          this.dataToUse["tickers"].map((ticker, index) => {
            const rows = [];
            rows.push(
              { type: "string", value: ticker },
              { type: "string", value: "Period" },
            );
            xlsxRows.push([]);
            xlsxRows.push(rows);
            this.chartInstance.options.series.map((item) => {
              if (item.name !== "") {
                const magnitude = `${
                  this.dataToUse["lineInfo"][this.currentItem] &&
                  this.dataToUse["lineInfo"][this.currentItem].magnitude &&
                  this.dataToUse["lineInfo"][this.currentItem].magnitude !==
                    "" &&
                  this.dataToUse["lineInfo"][this.currentItem].magnitude !==
                    "Abs" &&
                  this.dataToUse["scaleType"] !== 2 &&
                  this.dataToUse["dataView"] !== "yoy"
                    ? `(in ${
                        this.dataToUse["lineInfo"][this.currentItem].magnitude
                      })`
                    : ""
                }`;
                row.push({
                  type: "string",
                  value: `${
                    this.dataToUse["lineInfo"][this.currentItem]["name"]
                  }${magnitude}`,
                });
                if (!item["custom"].isLineItem) {
                  if (
                    item.name.split("-")[0] === this.dataToUse["tickers"][index]
                  ) {
                    row.length ? row.splice(1) : null;
                    row.push({
                      type: "string",
                      value: item.name.split("-").slice(1).join("-"),
                    });
                    const dateRow = [
                      { type: "string", value: "" },
                      { type: "string", value: "" },
                    ];
                    item["data"].length &&
                      item["data"].map((d) => {
                        const xDate = Highcharts.dateFormat("%d-%b-%Y", d.x);
                        dateRow.push({ type: "string", value: xDate });
                        const type =
                          typeof d.y === "number" ? "number" : "string";
                        row.push({ type: type, value: d.y });
                        return d;
                      });
                    xlsxRows.push(dateRow);
                    xlsxRows.push([...row]);
                  }
                }
              }
              return item;
            });
            return ticker;
          });
        }

        break;
      default:
        console.error(
          `Chart with type id ${this.dataToUse["chartTypeId"]} is not supported`,
        );
    }

    name = chartName || "chart";

    window.zipcelx({
      filename: name,
      sheet: {
        data: xlsxRows,
      },
    });
    // this.appService.logUserAction(
    //   {},
    //   "",
    //   "",
    //   "CHARTS_DOWNLOAD",
    //   {
    //     lines: this.lines,
    //     tickers: this.rowTickers,
    //     chart_id: this.chartId,
    //     chart_type_id: this.chartTypeId,
    //     downloadInfo: "Export_Value",
    //   },
    //   "",
    //   "CHARTS_PAGE",
    // );
  }

  changeAxis(event) {
    this.isSecondaryAxisUpdateInProgress = true;
    this.dataToUse = {
      ...this.dataToUse,
      isSecondaryAxis: event,
    };
    this.chartInstance.redraw();
  }

  // onChartInstance() {}
  onChartInstance(chartInstance: Highcharts.Chart) {
    if (chartInstance) {
      this.chartInstance = chartInstance;
      this.chartInfoBottom = `${
        document.getElementsByClassName("highcharts-legend-box")[0]["height"][
          "baseVal"
        ]["value"] + 42
      }px`;
      this.isSecondaryAxisUpdateInProgress = false;
    }
  }
}
